<template>
  <section class="main">
    <loader v-if="!isLoaded"></loader>
    <div v-show="isLoaded" class="main__row">
      <div class="main__content">
        <div class="section heading">
          <div
            class="heading__icon"
            :class="{ heading__icon_vertical: type === 'books' }"
            :style="`background-image: url(${image})`"
          ></div>
          <div
            ref="desc"
            class="heading__desc"
            :class="{
              heading__desc_expanded: isDescExpanded,
              heading__desc_book: type === 'books',
            }"
          >
            <div class="heading__mobile">
              <div
                class="heading__icon-mobile"
                :class="{ 'heading__icon-mobile_vertical': type === 'books' }"
                :style="`background-image: url(${image})`"
              ></div>
              <h1 class="heading__title">
                <span class="heading__title-inner">{{ details.name }}</span
                ><span class="heading__badge" v-if="isTweaked">{{
                  $t("TWEAKED")
                }}</span>
              </h1>
            </div>
            <div ref="desc__inner">
              <html-with-links
                tag="p"
                class="heading__text"
                :html="details.description"
              ></html-with-links>
            </div>
            <a href="#" class="more" @click.prevent="isDescExpanded = true">{{
              $t("VIEW MORE")
            }}</a>
          </div>
        </div>
        <div class="section" v-if="details.screenshots">
          <h2 class="h2">
            {{ $t("SCREENSHOTS") }}
            <ul class="select">
              <li
                v-for="(value, key) in details.screenshots"
                class="select__tab"
                :class="{ select__tab_active: screenshotTab === key }"
                @click="screenshotTab = key"
                :key="key"
              >
                {{ beautifyDevice(key) }}
              </li>
            </ul>
          </h2>
          <ul
            class="screenshots"
            v-for="(array, key) in details.screenshots"
            :key="key"
            v-show="screenshotTab === key"
          >
            <li
              class="screenshots__item"
              v-for="screenshot in array"
              :key="screenshot.src"
            >
              <img class="screenshots__img" :src="screenshot.src" />
            </li>
          </ul>
        </div>
      </div>
      <sidebar
        :isTweaked="isTweaked"
        :tweakedApps="tweakedApps"
        :type="type"
        :currentVersion="details.version"
        :versions="versions"
        :id="details.id"
        :originalId="details.original_trackid"
        :originalType="details.original_section"
        class="main__sidebar"
      />
    </div>
    <div v-show="isLoaded" class="main__content main__content_ptop">
      <div v-if="type !== 'books' && details.whatsnew" class="section">
        <h2 class="h2">{{ $t("WHATSNEW") }}</h2>
        <div
          ref="changelog"
          class="changelog"
          :class="{ changelog_expanded: isChangelogExpanded }"
        >
          <p ref="changelog__inner" v-html="details.whatsnew"></p>
          <a
            href="#"
            class="more"
            @click.prevent="isChangelogExpanded = true"
            >{{ $t("VIEW MORE") }}</a
          >
        </div>
      </div>
      <div class="section section_noborder">
        <h2 class="h2 h2_badge">
          {{ $t("INFORMATION") }}
          <span class="info-badge">{{ details.compatibility_string }}</span>
        </h2>
        <div class="info">
          <ul class="info__list">
            <li class="info__item" v-if="details.pname && +details.artist_id">
              <span class="info__intro">{{ $t("DEVELOPER") }}:</span>
              <router-link
                class="info__link"
                :to="`/search/?type=${type}&dev=${details.artist_id}`"
                >{{ details.pname }}</router-link
              >
            </li>
            <li class="info__item" v-if="details.pwebsite">
              <span class="info__intro">{{ $t("WEBSITE") }}:</span>
              <a class="info__link" :href="details.pwebsite" target="_blank">{{
                $t("VISIT")
              }}</a>
            </li>
            <li class="info__item" v-if="details.price">
              <span class="info__intro">{{ $t("PRICE") }}:</span>
              {{ $t((details.price || "").toUpperCase()) }}
            </li>
            <li
              class="info__item"
              v-if="genres[type] && genres[type][details.genre_id]"
            >
              <span class="info__intro">{{ $t("CATEGORY") }}:</span>
              <router-link
                class="info__link"
                :to="`/search/?type=${type}&genre=${details.genre_id}`"
              >
                {{
                  $t((genres[type][details.genre_id].name || "").toUpperCase())
                }}</router-link
              >
            </li>
            <li
              class="info__item"
              v-if="this.details.last_parse_itunes.customer_rating"
            >
              <span class="info__intro">{{ $t("CUSTOMER RATINGS") }}:</span>
              {{ this.details.last_parse_itunes.customer_rating }}
            </li>
            <li
              class="info__item"
              v-if="
                type !== 'books' && this.details.last_parse_itunes.published
              "
            >
              <span class="info__intro">{{ $t("UPDATED") }}:</span>
              {{ this.details.last_parse_itunes.published }}
            </li>
            <li
              class="info__item"
              v-if="type !== 'books' && this.details.last_parse_itunes.version"
            >
              <span class="info__intro">{{ $t("VERSION") }}:</span>
              {{ this.details.last_parse_itunes.version }}
            </li>
            <li class="info__item" v-if="this.details.last_parse_itunes.size">
              <span class="info__intro">{{ $t("SIZE") }}:</span>
              {{ this.details.last_parse_itunes.size }}
            </li>
            <li
              class="info__item"
              v-if="this.details.last_parse_itunes.languages"
            >
              <span class="info__intro">{{ $t("LANGUAGE") }}:</span>
              {{ this.details.last_parse_itunes.languages }}
            </li>
            <li class="info__item" v-if="this.details.last_parse_itunes.seller">
              <span class="info__intro">{{ $t("SELLER") }}:</span>
              {{ this.details.last_parse_itunes.seller }}
            </li>
            <li
              class="info__item"
              v-if="this.details.last_parse_itunes.publisher"
            >
              <span class="info__intro">{{ $t("PUBLISHER") }}:</span>
              {{ this.details.last_parse_itunes.publisher }}
            </li>
          </ul>
          <ul class="info__downloads">
            <li class="info__downloads-heading">{{ $t("DOWNLOADS") }}</li>
            <li class="info__download">
              {{ details.clicks_day }} {{ $t("TODAY") }}
            </li>
            <li class="info__download">
              {{ details.clicks_week }} {{ $t("THIS WEEK") }}
            </li>
            <li class="info__download">
              {{ details.clicks_month }} {{ $t("THIS MONTH") }}
            </li>
            <li class="info__download">
              {{ details.clicks_year }} {{ $t("THIS YEAR") }}
            </li>
            <li class="info__downloads-total">
              {{ details.clicks_all }} {{ $t("TOTAL") }}
            </li>
          </ul>
        </div>
      </div>
      <vue-disqus
        class="disqus"
        shortname="appdbcc"
        :identifier="`view-${type}-${$route.params.id}`"
        :language="$store.getters.lang"
        :key="$store.getters.lang + $route.params.id"
      ></vue-disqus>
    </div>
  </section>
</template>

<script>
import Sidebar from '@/components/AppSidebar/index.vue';
import Loader from '@/components/Loader';
import HtmlWithLinks from '@/components/HtmlWithLinks';

export default {
  components: { Sidebar, Loader, HtmlWithLinks },
  data() {
    return {
      requestCounter: 0,
      isDescExpanded: false,
      screenshotTab: 'iphone',
      isChangelogExpanded: false,
      details: {
        image: '',
        last_parse_itunes: {
          artist: {},
          raw_parse_itunes: {},
        },
      },
      versions: {},
      isLoaded: false,
    };
  },
  computed: {
    title() {
      return this.details.name;
    },
    id() {
      return this.$route.params.id;
    },
    isTweaked() {
      return this.details.original_trackid && this.details.original_trackid !== '0';
    },
    tweakedApps() {
      const versions = this.details.tweaked_versions;
      if (!versions) {
        return [];
      }

      const apps = [];
      Object.keys(versions).forEach((type) => {
        apps.push(...versions[type].map(app => ({ type, id: app.trackid, name: app.name })));
      });

      return apps;
    },
    genres() {
      return this.$store.getters.genres;
    },
    type() {
      return this.$route.params.type;
    },
    image() {
      let image = '';
      try {
        image = this.details.last_parse_itunes.raw_parse_itunes.artworkUrl512;
      } catch {
        // eslint-disable-next-line prefer-destructuring
        image = this.details.image;
      }
      return image || this.details.image;
    },
  },
  methods: {
    beautifyDevice(device) {
      const dict = {
        iphone: 'iPhone',
        ipad: 'iPad',
        appletv: 'Apple TV',
        osx: 'macOS',
      };

      return dict[device] || device;
    },
    async handleTriggers() {
      await this.$nextTick();

      const descWrapper = this.$refs.desc && this.$refs.desc.getBoundingClientRect().bottom;
      const descText = this.$refs.desc__inner
        && this.$refs.desc__inner.getBoundingClientRect().bottom;

      if (descText <= descWrapper) {
        this.isDescExpanded = true;
      }

      if (this.$refs.changelog__inner
        && this.$refs.changelog__inner.getBoundingClientRect().height <= 85) {
        this.isChangelogExpanded = true;
      }
    },
    getInfo() {
      this.isLoaded = false;
      return this.$api.getAppDetails({
        type: this.type,
        trackid: this.id,
      })
        .then((data) => {
          if (!data.length) {
            this.$router.push({
              name: 'error',
              params: {
                message: '404_CONTENT_MESSAGE',
                link: '/',
                linkText: 'GO_TO_MAIN_PAGE',
              },
            });
            return null;
          }

          this.details = { ...data[0] };
          try {
            this.details.screenshots = JSON.parse(this.details.screenshots);
            // eslint-disable-next-line
            this.screenshotTab = Object.keys(this.details.screenshots)[0];
          } catch {
            this.details.screenshots = null;
          }

          try {
            this.details.last_parse_itunes = JSON.parse(this.details.last_parse_itunes) || {
              artist: {},
              raw_parse_itunes: {},
            };
          } catch {
            this.details.last_parse_itunes = {
              artist: {},
              raw_parse_itunes: {},
            };
          }

          return this.$api.getLinks({
            type: this.type,
            trackid: this.id,
          });
        })
        .then((data) => {
          this.versions = (data && data[this.id]) || [];
          this.isLoaded = true;
        });
    },
  },
  watch: {
    isLoaded(newVal) {
      if (newVal) {
        this.handleTriggers();
      }
    },
    // eslint-disable-next-line
    '$route.params.id': function () {
      this.getInfo();
    },
  },
  serverPrefetch() {
    return this.getInfo();
  },
  created() {
    this.getInfo();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

@mixin gradient {
  &:before {
    content: "";
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 50px;
    background-image: linear-gradient(
      0deg,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.8),
      rgba(255, 255, 255, 0)
    );
    pointer-events: none;
  }

  &_expanded {
    max-height: unset;

    &:before,
    .more {
      display: none;
    }
  }
}

.changelog,
.heading__text {
  &::v-deep {
    img {
      max-width: 100%;
      height: auto;
    }

    p {
      &:not(:last-child) {
        margin-bottom: 10px;
      }
    }

    strong {
      font-weight: 700;
    }

    ul,
    ol {
      &:not(:last-child) {
        margin-bottom: 7px;
      }
    }

    ol {
      list-style: decimal inside;
    }

    ul {
      list-style: disc inside;
    }

    li {
      margin-bottom: 3px;
    }
  }
}

.main {
  display: flex;
  flex-wrap: wrap;

  &__row {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }

  &__content {
    max-width: 800px;
    width: 100%;

    &_ptop {
      padding-top: 30px;

      @include notebook {
        padding-top: 10px;
      }
    }

    @include notebook {
      max-width: 100%;
    }
  }

  &__sidebar {
    width: 370px;
    position: absolute;
    right: 0;
    top: 0;

    @include notebook {
      position: static;
      width: 100%;
    }
  }
}

.section {
  padding-bottom: 30px;
  border-bottom: 1px solid #e5e5e5;

  @include notebook {
    padding-bottom: 10px;
  }

  &_noborder {
    border: none;
  }

  &:not(:first-child) {
    padding-top: 30px;

    @include notebook {
      padding-top: 10px;
    }
  }
}

.heading {
  display: flex;

  &__desc {
    max-height: 227px;
    overflow: hidden;
    position: relative;

    &_book {
      max-height: 340px;
    }

    @include gradient;
  }

  &__mobile {
    @include notebook {
      display: flex;
    }
  }

  &__icon {
    flex: 0 0 227px;
    height: 227px;
    margin-right: 27px;
    border-radius: 40px;

    &:not(&_vertical) {
      border-radius: 22.5%;
      mask: url(../assets/ios-mask.svg) center/100% 100% no-repeat;
    }

    @include notebook {
      display: none;
    }

    &_vertical {
      height: 340px;
    }

    &,
    &-mobile {
      background-size: cover;
      background-position: center;
    }

    &-mobile {
      display: none;

      @include notebook {
        display: block;
        width: 64px;
        flex: 0 0 64px;
        height: 64px;
        border-radius: 12px;
        margin-right: 10px;
      }

      &_vertical {
        height: 96px;
      }
    }
  }

  &__title {
    display: flex;
    align-items: center;
    color: #181f25;
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 10px;
    min-width: 0;

    @include notebook {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      margin-bottom: 0;
    }

    &-inner {
      @include notebook {
        width: 100%;
      }
    }
  }

  &__badge {
    padding: 5px;
    color: #2b2b05;
    text-transform: uppercase;
    font-size: 14px;
    border-radius: 2px;
    background-color: #fff5cd;
    margin-left: 11px;

    @include notebook {
      margin-left: 0;
      margin-top: 5px;
    }
  }

  &__text {
    line-height: 16px;

    @include notebook {
      margin-top: 15px;
    }
  }

  &__separator {
    content: "";
    display: block;
    margin-top: 10px;
  }
}

.more {
  display: flex;
  align-items: center;
  color: $primary;
  font-weight: 500;
  position: absolute;
  left: 0;
  bottom: 0;

  @include notebook {
    right: 0;
    justify-content: center;
  }

  &:after {
    content: "";
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 4px 4px 0 4px;
    border-color: $primary transparent transparent transparent;
    margin-left: 3px;
    margin-top: 2px;
  }
}

.h2 {
  color: #181f25;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  @include notebook {
    margin-bottom: 10px;
  }

  &_badge {
    @include notebook {
      flex-direction: column;
      align-items: flex-start;
    }
  }
}

.select {
  display: flex;
  margin-left: 20px;
  border-radius: 2px;

  @include notebook {
    margin-left: 10px;
  }

  &__tab {
    position: relative;
    color: $text-primary;
    font-size: 14px;
    font-weight: 400;
    flex-grow: 1;
    padding: 3px 25px;
    border: 1px solid rgba(96, 112, 128, 0.4);
    cursor: pointer;

    &:first-child {
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
    }

    &:last-child {
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
    }

    &:before,
    &:after {
      content: "";
      display: none;
      position: absolute;
      top: -1px;
      bottom: -1px;
      width: 1px;
      background-color: rgba(96, 112, 128, 0.4);
    }

    &_active {
      background-color: $lighter-primary;
      border-color: lighten($primary, 30%);
      z-index: 1;

      &:before,
      &:after {
        background-color: lighten($primary, 30%);
      }
    }

    &:not(:first-child) {
      border-left: none;

      &:before {
        display: block;
        left: -1px;
      }
    }

    &:not(:last-child) {
      border-right: none;

      &:after {
        display: block;
        right: 0;
      }
    }
  }
}

.screenshots {
  display: flex;
  overflow: scroll;
  padding-bottom: 15px;

  &__item {
    &:not(:last-child) {
      margin-right: 5px;
    }
  }

  &__img {
    height: 350px;
  }
}

.changelog {
  display: inline-block;
  line-height: 16px;
  max-height: 85px;
  overflow: hidden;
  position: relative;
  @include gradient;
}

.ul {
  list-style: disc inside;
  margin-top: 5px;
}

.info-badge {
  background: $lighter-primary;
  border: 1px solid lighten($primary, 45%);
  border-radius: 2px;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 400;
  color: $text-primary;
  margin-left: 10px;

  @include notebook {
    margin-left: 0;
    margin-top: 5px;
  }
}

.info {
  display: flex;

  @include notebook {
    flex-direction: column;
  }

  &__item {
    margin-bottom: 5px;
    line-height: 20px;
  }

  &__link {
    color: $primary;
    font-weight: 500;
  }

  &__intro {
    color: #525d66;
    margin-right: 15px;
  }

  &__downloads {
    margin-left: auto;

    @include notebook {
      margin-left: 0;
      margin-top: 10px;
    }

    &-heading {
      color: #181f25;
      font-weight: 600;
      font-size: 18px;
      margin-bottom: 5px;
    }

    &-total {
      font-weight: 500;
      line-height: 20px;
    }
  }

  &__download {
    margin-bottom: 5px;
    line-height: 20px;
  }
}

.disqus {
  margin-top: 30px;

  @include notebook {
    margin-top: 10px;
  }
}
</style>
